import * as React from "react";

import { api } from "@repo/backend";
import { Button } from "@repo/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/components/dialog";
import { useQuery } from "convex/react";
import { XIcon } from "lucide-react";

import { useMediaDayStatusLanguage } from "./status-language";

type VerificationTargetId =
  (typeof api.mediaDayStatus.getPublicBoardVerificationFiles)["_args"]["targetIds"][number];

export type VerificationSummary = {
  targetId: VerificationTargetId;
  stage: "sourceJpg" | "cutout" | "cropped" | "teamcomp" | "editorialSource" | "editorialEdited";
  status: "pending" | "partial" | "verified";
  expected: number;
  found: number;
  missing: number;
  lastSuccessfulCheckAt: number | null;
  syncHealth: "pending" | "healthy" | "degraded";
  openFolderUrl: string | null;
};

const stageLabel = {
  sourceJpg: "Source JPG",
  cutout: "Cutout",
  cropped: "Cropped",
  teamcomp: "Teamcomp",
  editorialSource: "Editorial source",
  editorialEdited: "Editorial edited JPG",
};

export function VerificationCell({
  token,
  subject,
  items,
  expectedFiles,
}: {
  token: string;
  subject: string;
  items: (VerificationSummary | null | undefined)[];
  expectedFiles?: number;
}) {
  const { locale, t } = useMediaDayStatusLanguage();
  const summaryId = React.useId();
  const [open, setOpen] = React.useState(false);
  const verification = items.filter(Boolean) as VerificationSummary[];
  const files = useQuery(
    api.mediaDayStatus.getPublicBoardVerificationFiles,
    open && verification.length
      ? { token, targetIds: verification.map((item) => item.targetId) }
      : "skip",
  );
  const filesByTargetId = new Map(files?.map((item) => [String(item.targetId), item]));
  if (!verification.length && !expectedFiles)
    return <span className="text-[11px] font-semibold text-neutral-600">{t("Pending")}</span>;

  const found = verification.reduce((total, item) => total + item.found, 0);
  const manifestExpected = verification.reduce((total, item) => total + item.expected, 0);
  const expected = Math.max(manifestExpected, expectedFiles ?? 0);
  const delayed = verification.some((item) => item.syncHealth === "degraded");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <button
            type="button"
            aria-describedby={summaryId}
            className="mx-auto flex min-h-10 w-full flex-col items-center justify-center px-1 py-1 text-[11px] font-bold tabular-nums hover:bg-black/5"
          >
            <span id={summaryId}>
              {found === expected
                ? `${t("Verified")} ${found}/${expected}`
                : `${found}/${expected}`}
            </span>
            {delayed ? <span className="font-medium text-red-800">{t("Sync delayed")}</span> : null}
          </button>
        }
      />
      <DialogContent showCloseButton={false} className="max-w-xl">
        <DialogClose
          aria-label={t("Close")}
          render={
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              className="absolute top-2 right-2"
            />
          }
        >
          <XIcon className="size-4" />
        </DialogClose>
        <DialogHeader>
          <DialogTitle>
            {subject} — {t("file verification")}
          </DialogTitle>
          <DialogDescription>
            {t("Exact filenames confirmed in the configured Voxis Working Folders.")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[65vh] gap-4 overflow-auto">
          {manifestExpected < expected ? (
            <p className="rounded-md border p-3 text-sm text-muted-foreground">
              {t("Waiting for the verification manifest to include all expected files.")} (
              {expected})
            </p>
          ) : null}
          {verification.map((item, index) => (
            <section key={`${item.stage}-${index}`} className="rounded-md border p-3">
              <div>
                <h3 className="text-sm font-semibold">{t(stageLabel[item.stage])}</h3>
                <p className="text-xs text-muted-foreground">
                  {t("Expected")} {item.expected} · {t("Found")} {item.found}
                </p>
              </div>
              {item.status === "pending" ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  {t("Waiting for the first check.")}
                </p>
              ) : item.missing ? (
                files === undefined ? (
                  <p className="mt-3 text-sm text-muted-foreground">{t("Loading filenames…")}</p>
                ) : (
                  <div className="mt-3">
                    <div className="mb-1 text-xs font-semibold text-muted-foreground uppercase">
                      {t("Missing")}
                    </div>
                    <ul className="grid gap-1 font-mono text-xs">
                      {(filesByTargetId.get(item.targetId)?.missingFiles ?? []).map((filename) => (
                        <li key={filename} className="rounded bg-muted px-2 py-1 break-all">
                          {filename}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ) : (
                <p className="mt-3 text-sm font-medium text-green-700">
                  {t("All expected files confirmed.")}
                </p>
              )}
              <div className="mt-3 text-xs text-muted-foreground">
                {item.lastSuccessfulCheckAt
                  ? `${t("Last successful check")}: ${new Date(item.lastSuccessfulCheckAt).toLocaleString(locale)}`
                  : t("No successful check yet")}
                {item.syncHealth === "degraded" ? ` · ${t("Voxis sync delayed")}` : ""}
              </div>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
